import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { LinkToCmmdSheet } from "./project/LinkToCmmdSheet";
import type { UseCmmdProjectLinkResult } from "../hooks/useCmmdProjectLink";

export interface StartThreadPromptState {
  readonly itemId: string;
  readonly title: string;
}

export interface MissingForgeProjectPromptState {
  readonly itemId: string;
  readonly taskTitle: string;
  readonly cmmdProjectId: string;
  readonly cmmdProjectTitle: string;
}

export interface NoActiveThreadStateDialogsProps {
  readonly startThreadPrompt: StartThreadPromptState | null;
  readonly missingForgeProjectPrompt: MissingForgeProjectPromptState | null;
  readonly missingForgeProjectImporting: boolean;
  readonly projectLinkSheetOpen: boolean;
  readonly projectLinkSheetMode: "link" | "create";
  readonly cmmdProjectLink: UseCmmdProjectLinkResult;
  readonly onStartThreadPromptOpenChange: (open: boolean) => void;
  readonly onConfirmStartThreadPrompt: () => void;
  readonly onMissingForgeProjectPromptChange: (
    prompt: MissingForgeProjectPromptState | null,
  ) => void;
  readonly onProjectLinkSheetModeChange: (mode: "link" | "create") => void;
  readonly onProjectLinkSheetOpenChange: (open: boolean) => void;
  readonly onImportMissingForgeProject: () => void;
}

export function NoActiveThreadStateDialogs(props: NoActiveThreadStateDialogsProps) {
  return (
    <>
      <StartThreadPromptDialog
        prompt={props.startThreadPrompt}
        onOpenChange={props.onStartThreadPromptOpenChange}
        onConfirm={props.onConfirmStartThreadPrompt}
      />
      <MissingForgeProjectDialog
        prompt={props.missingForgeProjectPrompt}
        importing={props.missingForgeProjectImporting}
        onPromptChange={props.onMissingForgeProjectPromptChange}
        onProjectLinkSheetModeChange={props.onProjectLinkSheetModeChange}
        onProjectLinkSheetOpenChange={props.onProjectLinkSheetOpenChange}
        onImport={props.onImportMissingForgeProject}
      />
      {/* Reached from the "Project not in Forge yet" prompt, which is a
          Command Center flow with no project of its own — hence the empty
          `cwd` and default name. */}
      <LinkToCmmdSheet
        open={props.projectLinkSheetOpen}
        onOpenChange={props.onProjectLinkSheetOpenChange}
        cwd=""
        existingLink={props.cmmdProjectLink.link}
        initialMode={props.projectLinkSheetMode}
        onLink={props.cmmdProjectLink.setLink}
        onUnlink={props.cmmdProjectLink.deleteLink}
      />
    </>
  );
}

function StartThreadPromptDialog({
  prompt,
  onOpenChange,
  onConfirm,
}: {
  readonly prompt: StartThreadPromptState | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => void;
}) {
  return (
    <AlertDialog open={prompt !== null} onOpenChange={onOpenChange}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>Start Thread Too?</AlertDialogTitle>
          <AlertDialogDescription>
            You moved &quot;{prompt?.title ?? "this task"}&quot; to In Progress. Start a thread now
            so the agent can begin immediately while you stay in Kanban?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />}>
            No, just move status
          </AlertDialogClose>
          <Button onClick={onConfirm}>Yes, start thread</Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

function MissingForgeProjectDialog({
  prompt,
  importing,
  onPromptChange,
  onProjectLinkSheetModeChange,
  onProjectLinkSheetOpenChange,
  onImport,
}: {
  readonly prompt: MissingForgeProjectPromptState | null;
  readonly importing: boolean;
  readonly onPromptChange: (prompt: MissingForgeProjectPromptState | null) => void;
  readonly onProjectLinkSheetModeChange: (mode: "link" | "create") => void;
  readonly onProjectLinkSheetOpenChange: (open: boolean) => void;
  readonly onImport: () => void;
}) {
  return (
    <AlertDialog
      open={prompt !== null}
      onOpenChange={(open) => {
        if (!open && !importing) onPromptChange(null);
      }}
    >
      <AlertDialogPopup className="max-w-md sm:rounded-lg" bottomStickOnMobile>
        <AlertDialogHeader className="gap-3 text-left">
          <AlertDialogTitle className="text-lg">Project not in Forge yet</AlertDialogTitle>
          <AlertDialogDescription className="leading-5">
            This task belongs to a CMMD project that hasn&apos;t been set up in Forge. Import it as
            a new project, or link it to an existing one.
          </AlertDialogDescription>
          <div className="rounded-md border border-border/70 bg-muted/35 px-3 py-2 text-sm font-medium leading-5 text-foreground">
            {prompt?.cmmdProjectTitle ?? "CMMD project"}
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter variant="bare" className="flex-col gap-2 pt-0">
          <div className="flex gap-2">
            <AlertDialogClose
              render={<Button variant="outline" disabled={importing} />}
              className="flex-1"
            >
              Cancel
            </AlertDialogClose>
            <Button
              variant="outline"
              className="flex-1"
              disabled={importing}
              onClick={() => {
                onPromptChange(null);
                onProjectLinkSheetModeChange("link");
                onProjectLinkSheetOpenChange(true);
              }}
            >
              Link existing
            </Button>
          </div>
          <Button className="w-full" onClick={onImport} disabled={importing} aria-busy={importing}>
            {importing ? "Importing project..." : "Import as new project"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
